/**
 * EarningsVehicles — per-vehicle earnings breakdown.
 *
 * Extracted from the "By Vehicle" group-by in EarningsRecords into
 * its own dedicated page for a focused vehicle-level financial view.
 *
 * Features:
 * - Vehicle column shows reg_number (primary) + VIN (secondary)
 * - Search bar filters by reg number, VIN, make, or model
 * - Inline FeeBar split visualization
 * - Pagination (15 per page)
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, formatCurrency, CHART_COLORS,
} from '@/lib/format';
import { startOfMonth, subMonths, endOfMonth } from 'date-fns';
import { captureError } from '@/lib/logger';
import { ListPagination } from '@/components/ListPagination';
import SectionDrawer from '@/components/navigation/SectionDrawer';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';

const PAGE_SIZE = 15;

interface EarningsRow {
    group_key: string;
    group_label: string;
    vehicle_count: number;
    gross_repayments: number;
    management_fee_amount: number;
    partner_portion: number;
    maintenance_cost: number;
    net_earnings: number;
    repayment_count: number;
}

interface VehicleInfo {
    id: number;
    make: string;
    model: string;
    vin: string;
    vehicle_registrations: { number: string | null } | { number: string | null }[] | null;
}

/** Inline horizontal stacked bar */
function FeeBar({ gross, partnerPortion }: { gross: number; partnerPortion: number }) {
    if (gross <= 0) return <div className="h-2 rounded-full bg-muted w-full" />;
    const partnerPct = (partnerPortion / gross) * 100;
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
                <div
                    className="h-full rounded-l-full transition-all duration-300"
                    style={{ width: `${partnerPct}%`, backgroundColor: CHART_COLORS.slate900 }}
                />
                <div
                    className="h-full rounded-r-full transition-all duration-300"
                    style={{ width: `${100 - partnerPct}%`, backgroundColor: CHART_COLORS.slate300 }}
                />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right shrink-0">
                {partnerPct.toFixed(2)}%
            </span>
        </div>
    );
}

export default function EarningsVehicles() {
    const { partnerId, signOut } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth');
    };

    const handleNavChange = (section: string) => {
        navigate(`/${section}`);
    };

    // Fetch earnings grouped by vehicle
    const { data: earningsData, isLoading: earningsLoading } = useQuery({
        queryKey: ['partner-earnings-vehicles', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'vehicle',
            });
            if (error) { captureError(error); throw error; }
            return data as EarningsRow[];
        },
        enabled: !!partnerId,
    });

    // Fetch vehicle details (reg_number + VIN) for all partner vehicles
    const { data: vehicleMap } = useQuery({
        queryKey: ['partner-vehicle-details', partnerId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicles')
                .select('id, make, model, vin, vehicle_registrations(number)')
                .eq('partner_id', partnerId!);
            if (error) { captureError(error); throw error; }

            const map = new Map<string, VehicleInfo>();
            (data || []).forEach((v: any) => {
                map.set(String(v.id), v as VehicleInfo);
            });
            return map;
        },
        enabled: !!partnerId,
    });

    // Combine earnings + vehicle details, apply search
    const filteredData = useMemo(() => {
        if (!earningsData) return [];

        return earningsData.filter(row => {
            if (!search) return true;
            const q = search.toLowerCase();
            const vehicle = vehicleMap?.get(row.group_key);
            const reg = vehicle?.vehicle_registrations;
            const regNumber = Array.isArray(reg) ? reg[0]?.number || '' : reg?.number || '';
            const vin = vehicle?.vin || '';
            const make = vehicle?.make || '';
            const model = vehicle?.model || '';

            return (
                regNumber.toLowerCase().includes(q) ||
                vin.toLowerCase().includes(q) ||
                make.toLowerCase().includes(q) ||
                model.toLowerCase().includes(q) ||
                row.group_label.toLowerCase().includes(q)
            );
        });
    }, [earningsData, vehicleMap, search]);

    // Reset page on filter changes
    useEffect(() => { setCurrentPage(1); }, [search, monthRange.from, monthRange.to]);

    // Pagination
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    /** Render vehicle identity: reg number (primary) + VIN (secondary) */
    function VehicleCell({ row }: { row: EarningsRow }) {
        const vehicle = vehicleMap?.get(row.group_key);
        const reg = vehicle?.vehicle_registrations;
        const regNumber = Array.isArray(reg) ? reg[0]?.number : reg?.number;
        const vin = vehicle?.vin;

        if (regNumber) {
            return (
                <div>
                    <div className="font-mono text-sm font-medium">{regNumber}</div>
                    {vin && <div className="text-xs text-muted-foreground font-mono">{vin}</div>}
                </div>
            );
        }

        // Fallback: show VIN if no reg, or the label from the RPC
        if (vin) {
            return <span className="text-muted-foreground text-xs font-mono">{vin}</span>;
        }

        return <span className="text-sm">{row.group_label}</span>;
    }

    const isLoading = earningsLoading;

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer
                    activeSection="finance/vehicles"
                    onSectionChange={handleNavChange}
                    onSignOut={handleSignOut}
                />
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-semibold tracking-tight mb-2">Vehicle Earnings</h1>
                    <p className="text-muted-foreground">Earnings breakdown per vehicle.</p>
                </div>
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Search + Pagination */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by reg, VIN, make..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                {totalPages > 1 && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="space-y-3">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : filteredData.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                    {search ? 'No vehicles match your search.' : 'No earnings data for the selected period.'}
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="overflow-x-auto hidden lg:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[200px]">Vehicle</TableHead>
                                        <TableHead className="text-right">Count</TableHead>
                                        <TableHead className="text-right">Gross</TableHead>
                                        <TableHead className="w-[200px]">Split</TableHead>
                                        <TableHead className="text-right">Fee</TableHead>
                                        <TableHead className="text-right">Your Earnings</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedData.map((row) => {
                                        const feeRate = row.gross_repayments > 0
                                            ? (row.management_fee_amount / row.gross_repayments * 100)
                                            : 0;

                                        return (
                                            <TableRow key={row.group_key}>
                                                <TableCell><VehicleCell row={row} /></TableCell>
                                                <TableCell className="text-right tabular-nums">{row.repayment_count}</TableCell>
                                                <TableCell className="text-right tabular-nums">{formatCurrency(row.gross_repayments)}</TableCell>
                                                <TableCell>
                                                    <FeeBar
                                                        gross={row.gross_repayments}
                                                        partnerPortion={row.partner_portion}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="text-sm tabular-nums text-muted-foreground">
                                                        {formatCurrency(row.management_fee_amount)}
                                                        <span className="ml-1 text-xs">({feeRate.toFixed(2)}%)</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right font-medium tabular-nums">
                                                    {formatCurrency(row.partner_portion)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="lg:hidden space-y-3">
                        {paginatedData.map((row) => {
                            const feeRate = row.gross_repayments > 0
                                ? (row.management_fee_amount / row.gross_repayments * 100)
                                : 0;

                            return (
                                <Card key={row.group_key} className="border border-border/40">
                                    <CardContent className="p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <VehicleCell row={row} />
                                            <div className="text-xs text-muted-foreground">
                                                {row.repayment_count} repayments
                                            </div>
                                        </div>
                                        <FeeBar
                                            gross={row.gross_repayments}
                                            partnerPortion={row.partner_portion}
                                        />
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <div className="text-xs text-muted-foreground">Gross</div>
                                                <div className="text-sm font-medium tabular-nums">{formatCurrency(row.gross_repayments)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Fee ({feeRate.toFixed(2)}%)</div>
                                                <div className="text-sm text-muted-foreground tabular-nums">{formatCurrency(row.management_fee_amount)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-muted-foreground">Yours</div>
                                                <div className="text-sm font-semibold tabular-nums">{formatCurrency(row.partner_portion)}</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
